import {
  Address,
  ForwardFillMax,
  forwardFillMaxMenuItems,
  LocationResponse,
  Money,
} from '@traba/types'

import * as yup from 'yup'

export function getAddressString(address: Address, locationName?: string) {
  try {
    const { street1, street2, city, state, postalCode } = address

    const nameStr = locationName ? locationName + ', ' : ''
    const street2Str = street2 ? street2 + ', ' : ''

    return `${nameStr}${street1}, ${street2Str}${city}, ${state} ${postalCode}`
  } catch (err) {
    console.error(
      'stringUtils -> geAddressString() ERROR. Returning empty address string instead.',
      { address },
      err,
    )
    return ''
  }
}

export function truncatedStreet1AddressString(
  address: Address,
  truncationLength = 3,
) {
  const { street1 } = address
  return street1.split(' ').slice(0, truncationLength).join(' ')
}

export function getLocationNameOrTruncatedAddress(
  location: Pick<LocationResponse, 'name' | 'address'>,
  truncationLength?: number,
) {
  return (
    location.name ||
    truncatedStreet1AddressString(location.address, truncationLength)
  )
}

export function getInitials(firstName: string, lastName: string) {
  return `${firstName?.substring(0, 1)} ${lastName?.substring(0, 1)}`
}

const email = () => yup.string().email('Invalid email')
export const validateEmail = (str: string) => email().validateSync(str)

export const validateUniqueEmails = (emails: string[]) =>
  Array.from(
    new Set(
      emails.filter((e) => {
        try {
          validateEmail(e)
          return true
          // eslint-disable-next-line
        } catch (e) {
          return false
        }
      }),
    ),
  )

export const sortAlphabetically = (a: string, b: string) => {
  return a.localeCompare(b)
}

// Pulled from Ops Console codebase, but removing the '...' (could be in shared utils in the future)
export function truncateString(str: string, length = 6, showElipses = false) {
  if (str.length <= length) {
    return str
  }
  return showElipses
    ? `${str.substring(0, length)}...`
    : `${str.substring(0, length)}`
}

export function numToAlpha(num: number, lowercase?: boolean): string {
  const asciiBase = lowercase ? 97 : 65
  return String.fromCharCode(asciiBase + num)
}

export function getAlphabeticalLabelFromIndex(index: number): string {
  return String.fromCharCode(65 + index) + '.'
}

export function getMoneyString(
  money: Money | number | undefined,
  hideCents?: boolean | undefined,
) {
  if (money === undefined) {
    return ''
  }

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'USD', // eventually have this map based on the 'currency' property in the Money object
    minimumFractionDigits: hideCents ? 0 : 2,
    maximumFractionDigits: hideCents ? 0 : 2,
  }

  if (typeof money === 'number') {
    return money.toLocaleString(undefined, options)
  }

  return (money.amount / 100).toLocaleString(undefined, options)
}

/*
 * This function will capitalize the first letter of each
 * word in the string separated by the passed string
 * Example: bad_OVERBOOK -> Bad Overbook
 */
export function capitalizeFirstLetterOfEachWord(
  sentence: string,
  separator: string,
) {
  return sentence.split(separator).map(capitalize).join(' ')
}

function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

/**
 * Formats an address to a string with the following format `city, state postalCode`
 */
export function getAddressZipCodeString(address?: Partial<Address>): string {
  if (!address) {
    return ''
  }
  const { city, state, postalCode } = address
  return `${city ?? ''}, ${state ?? ''} ${postalCode ?? ''}`
}

export function makeEnumValueHumanReadable(enumValue: string) {
  return enumValue.replace(/_/g, ' ').toLowerCase()
}

export const getForwardFillMaxLabel = (value: ForwardFillMax): string => {
  const menuItem = forwardFillMaxMenuItems.find((item) => item.value === value)
  return menuItem?.label || value
}
